import { useEffect } from 'react';
import Hero from './components/Hero';
import Services from './components/Services';
import NayaxPayment from './components/NayaxPayment';
import About from './components/About';
import Locations from './components/Locations';
import Testimonials from './components/Testimonials';
import FAQ from './components/FAQ';
import Contact from './components/Contact';
import Footer from './components/Footer';
import Navbar from './components/Navbar';

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

export default function HomePage() {
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];

    // --- LocalBusiness ---
    const localBusiness = document.createElement('script');
    localBusiness.type = 'application/ld+json';
    localBusiness.id = 'schema-local-business';
    localBusiness.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "VE Goodies",
      "alternateName": "VE Goodies Vending",
      "image": "https://static.readdy.ai/image/e468a762cbf2c4de3f207872d8ecdaa7/249121c6a7dc8fcc1d538c9d07722d0f.png",
      "url": SITE_URL,
      "telephone": "+14699223413",
      "email": "vegoodiesllc@gmail.com",
      "priceRange": "Free Installation",
      "description": "VE Goodies provides free vending machine installation for offices, schools, and warehouses in Carrollton and across the Dallas-Fort Worth metroplex within a 50-mile radius. Healthy snacks, beverages, coffee service, cashless payments, and smart vending available.",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Carrollton",
        "addressRegion": "TX",
        "postalCode": "75006",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "32.9537",
        "longitude": "-96.8903"
      },
      "areaServed": [
        { "@type": "City", "name": "Little Elm" },
        { "@type": "City", "name": "Frisco" },
        { "@type": "City", "name": "Carrollton" },
        { "@type": "City", "name": "Plano" },
        { "@type": "City", "name": "Denton" },
        { "@type": "City", "name": "The Colony" },
        { "@type": "City", "name": "Lewisville" },
        { "@type": "City", "name": "Addison" },
        { "@type": "City", "name": "Dallas" },
        { "@type": "City", "name": "Fort Worth" }
      ],
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "08:00",
          "closes": "18:00"
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Vending Machine Services",
        "itemListElement": [
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Snack Vending Machine" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Beverage Vending Machine" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Combo Vending Machine" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Healthy Vending Machine" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Coffee Service" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Fresh Food Vending Machine" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Smart Vending Machine" } }
        ]
      },
      "sameAs": [
        "https://www.facebook.com/vegoodies",
        "https://www.instagram.com/vegoodies"
      ],
      "dateModified": today
    });
    document.head.appendChild(localBusiness);

    // --- WebSite ---
    const webSite = document.createElement('script');
    webSite.type = 'application/ld+json';
    webSite.id = 'schema-website';
    webSite.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "VE Goodies",
      "url": SITE_URL,
      "description": "Free vending machine installation for businesses in Little Elm and the Dallas-Fort Worth area.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${SITE_URL}/?s={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    });
    document.head.appendChild(webSite);

    // --- FAQPage ---
    const faqSchema = document.createElement('script');
    faqSchema.type = 'application/ld+json';
    faqSchema.id = 'schema-faq';
    faqSchema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does it cost to have a vending machine installed?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "There is no cost for installation! We provide free vending machine placement, installation, and setup. Our business model is based on product sales, so there are no upfront fees, rental charges, or hidden costs to your business."
          }
        },
        {
          "@type": "Question",
          "name": "What types of products can you stock in the machines?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We offer a wide variety of products including traditional snacks, healthy options, fresh food, beverages (both cold and hot), coffee service, and specialty items. We work with you to customize the product selection based on your employees' preferences and dietary needs."
          }
        },
        {
          "@type": "Question",
          "name": "How often are the machines restocked?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Restocking frequency depends on your location's usage, but typically we restock weekly or bi-weekly. We monitor inventory levels and adjust our schedule to ensure your machine never runs empty."
          }
        },
        {
          "@type": "Question",
          "name": "What happens if the machine breaks down?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We provide 24/7 emergency support for all our machines. If you experience any issues, simply call our support line and we'll dispatch a technician immediately. Most issues are resolved within 4 hours."
          }
        },
        {
          "@type": "Question",
          "name": "Do the machines accept credit cards and mobile payments?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! All our modern vending machines are equipped with cashless payment systems that accept credit cards, debit cards, Apple Pay, Google Pay, and other mobile payment options, in addition to traditional cash payments."
          }
        },
        {
          "@type": "Question",
          "name": "Is there a contract or long-term commitment required?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We offer flexible agreements with no long-term contracts required. You're free to discontinue service with 30 days' notice. Our goal is to earn your business through excellent service, not binding contracts."
          }
        },
        {
          "@type": "Question",
          "name": "Can we request specific brands or products?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Absolutely! We work with all major brands including Coca-Cola, Pepsi, Frito-Lay, Doritos, Snickers, and many more. During setup, we'll discuss your preferences and can conduct employee surveys to determine the most popular items."
          }
        }
      ]
    });
    document.head.appendChild(faqSchema);

    // --- Service ---
    const serviceSchema = document.createElement('script');
    serviceSchema.type = 'application/ld+json';
    serviceSchema.id = 'schema-service';
    serviceSchema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Vending Machine Services",
      "provider": {
        "@type": "LocalBusiness",
        "name": "VE Goodies",
        "telephone": "+14699223413",
        "email": "vegoodiesllc@gmail.com",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Carrollton",
          "addressRegion": "TX",
          "postalCode": "75006",
          "addressCountry": "US"
        }
      },
      "areaServed": [
        { "@type": "City", "name": "Carrollton" },
        { "@type": "City", "name": "Frisco" },
        { "@type": "City", "name": "Carrollton" },
        { "@type": "City", "name": "Plano" },
        { "@type": "City", "name": "Denton" },
        { "@type": "City", "name": "The Colony" },
        { "@type": "City", "name": "Lewisville" },
        { "@type": "City", "name": "Addison" },
        { "@type": "City", "name": "Dallas" },
        { "@type": "City", "name": "Fort Worth" }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Vending Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Full-Service Vending",
              "description": "Complete vending solutions with installation, stocking, and maintenance included at no cost to you"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Healthy Options",
              "description": "Nutritious snacks, fresh fruits, protein bars, organic beverages, and wellness-focused products for health-conscious teams"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Cashless Systems",
              "description": "Modern payment solutions including credit cards, mobile payments, Apple Pay, Google Pay, and contactless options"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Smart Vending Options",
              "description": "AI-powered machines with interactive touchscreens, remote monitoring, real-time inventory tracking, and data analytics"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Equipment Maintenance",
              "description": "Regular servicing, repairs, and upgrades to keep your machines running smoothly 24/7"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Regular Restocking",
              "description": "Frequent inventory checks and restocking to ensure your team always has fresh products available"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "24/7 Support",
              "description": "Round-the-clock customer service and technical support for any issues or questions"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Custom Solutions",
              "description": "Tailored vending programs designed to meet your specific business needs and preferences"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Inventory Management",
              "description": "Smart tracking systems and data analytics to optimize product selection and reduce waste"
            }
          }
        ]
      }
    });
    document.head.appendChild(serviceSchema);

    // --- Organization ---
    const organizationSchema = document.createElement('script');
    organizationSchema.type = 'application/ld+json';
    organizationSchema.id = 'schema-organization';
    organizationSchema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "VE Goodies",
      "url": SITE_URL,
      "logo": "https://static.readdy.ai/image/e468a762cbf2c4de3f207872d8ecdaa7/249121c6a7dc8fcc1d538c9d07722d0f.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+14699223413",
        "contactType": "Customer Service",
        "email": "vegoodiesllc@gmail.com",
        "areaServed": ["US"],
        "availableLanguage": ["English"]
      },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Carrollton",
        "addressRegion": "TX",
        "postalCode": "75006",
        "addressCountry": "US"
      },
      "areaServed": [
        { "@type": "City", "name": "Carrollton" },
        { "@type": "City", "name": "Frisco" },
        { "@type": "City", "name": "Carrollton" },
        { "@type": "City", "name": "Plano" },
        { "@type": "City", "name": "Denton" },
        { "@type": "City", "name": "The Colony" },
        { "@type": "City", "name": "Lewisville" },
        { "@type": "City", "name": "Addison" },
        { "@type": "City", "name": "Dallas" },
        { "@type": "City", "name": "Fort Worth" }
      ],
      "sameAs": [
        "https://www.facebook.com/vegoodies",
        "https://www.instagram.com/vegoodies"
      ]
    });
    document.head.appendChild(organizationSchema);

    return () => {
      document.getElementById('schema-local-business')?.remove();
      document.getElementById('schema-website')?.remove();
      document.getElementById('schema-faq')?.remove();
      document.getElementById('schema-service')?.remove();
      document.getElementById('schema-organization')?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Hero />
      <Services />
      <NayaxPayment />
      <About />
      <Locations />
      <Testimonials />
      <FAQ />
      <Contact />
      <Footer />
    </div>
  );
}